import React, { useState, useEffect } from 'react';
import './MySubmissions.css';
import '../styles/ResizableTable.css';
import { useUser } from '../contexts/UserContext';
import { useEditInitiative } from '../contexts/EditInitiativeContext';
import { initiativeService, Initiative } from '../services/api';
import axios from 'axios';
import { api } from '../config';
import { initResizableTables } from '../utils/resizableTables';
import Spinner from './Spinner';

const MySubmissions: React.FC = () => {
  const { user } = useUser();
  const { editData, setEditData, successMessage, setSuccessMessage, switchToSubmitTab } = useEditInitiative();
  const [submissions, setSubmissions] = useState<Initiative[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<Initiative | null>(null);
  const [recallMessage, setRecallMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isRecalling, setIsRecalling] = useState(false);
  const [isRenotifying, setIsRenotifying] = useState(false);
  const [renotifyMessage, setRenotifyMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [divisionReviewers, setDivisionReviewers] = useState<any[]>([]);
  const [isLoadingReviewers, setIsLoadingReviewers] = useState(false);
  const [displayMessage, setDisplayMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteSubmissionId, setDeleteSubmissionId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [workflowData, setWorkflowData] = useState<any>(null);
  const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(false);
  const [workflowSubmissionId, setWorkflowSubmissionId] = useState<number | null>(null);
  const [engagementSummary, setEngagementSummary] = useState<Record<string, { like_count: number; partner_count: number; liked_by: string[] }>>({});
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [partnerDetails, setPartnerDetails] = useState<Record<number, { id: number; email: string; message: string; created_at: string; status: string; owner_comment?: string }[]>>({});
  const [acceptModal, setAcceptModal] = useState<{ initiativeId: number; requestId: number } | null>(null);
  const [acceptComment, setAcceptComment] = useState('');
  const [isAccepting, setIsAccepting] = useState(false); 
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await initiativeService.getInitiatives();
        // Filter submissions by current user
        const userSubmissions = response.data.filter(
          (initiative: Initiative) => initiative.submitted_by?.toLowerCase() === user?.email?.toLowerCase()
        );
        setSubmissions(userSubmissions);
        // Fetch engagement summary for user's submissions
        if (userSubmissions.length > 0) {
          try {
            const ids = userSubmissions.map((i: Initiative) => i.id).join(',');
            const res = await axios.get(api(`/api/initiatives/engagements/summary?initiative_ids=${ids}`));
            setEngagementSummary(res.data);
          } catch { /* silent */ }
        }
      } catch (err: any) {
        setError('Failed to load submissions');
        console.error('Error fetching submissions:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (user?.email) {
      fetchSubmissions();
    }
  }, [user?.email]);

  // Initialize resizable tables when submissions load
  useEffect(() => {
    if (!isLoading && submissions.length > 0) {
      const timer = setTimeout(() => {
        initResizableTables();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isLoading, submissions.length]);

  // Handle Escape key to close modals
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showWorkflowModal && !isLoadingWorkflow) {
          setShowWorkflowModal(false);
          setWorkflowSubmissionId(null);
          setWorkflowData(null);
        } else if (showDeleteConfirm && !isDeleting) {
          setShowDeleteConfirm(false);
          setDeleteSubmissionId(null);
        } else if (selectedSubmission) {
          setSelectedSubmission(null);
          setRecallMessage(null);
        }
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [selectedSubmission, showWorkflowModal, isLoadingWorkflow, showDeleteConfirm, isDeleting]);

  // Fetch division reviewers when submission is selected
  useEffect(() => {
    if (selectedSubmission && selectedSubmission.division) {
      const fetchReviewers = async () => {
        try {
          setIsLoadingReviewers(true);
          const response = await axios.get(api(`/api/reviews/reviewers?division=${encodeURIComponent(selectedSubmission.division)}`));
          setDivisionReviewers(response.data);
        } catch (err) {
          console.error('Error fetching reviewers:', err);
          setDivisionReviewers([]);
        } finally {
          setIsLoadingReviewers(false);
        }
      };
      fetchReviewers();
    } else {
      setDivisionReviewers([]);
    }
  }, [selectedSubmission?.id, selectedSubmission?.division]);

  // Handle success message from resubmit
  useEffect(() => {
    if (successMessage) {
      // Just clear the message, don't display it
      setSuccessMessage(null);
      
      // Reload submissions to show the new submission
      const fetchSubmissions = async () => {
        try {
          const response = await initiativeService.getInitiatives();
          const userSubmissions = response.data.filter(
            (initiative: Initiative) => initiative.submitted_by?.toLowerCase() === user?.email?.toLowerCase()
          );
          setSubmissions(userSubmissions);
        } catch (err: any) {
          console.error('Error fetching submissions:', err);
        }
      };
      
      if (user?.email) {
        fetchSubmissions();
      }
    }
  }, [successMessage, user?.email, setSuccessMessage]);

  const handleRecall = async () => {
    if (!selectedSubmission) return;

    try {
      setIsRecalling(true);
      setRecallMessage(null);
      await initiativeService.recallInitiative(selectedSubmission.id);
      
      // Update the status in the list
      setSubmissions(submissions.map(sub =>
        sub.id === selectedSubmission.id
          ? { ...sub, status: 'recalled' as any }
          : sub
      ));
      
      // Update the selected submission
      setSelectedSubmission({ ...selectedSubmission, status: 'recalled' as any });
      setRecallMessage({ type: 'success', text: 'Initiative recalled successfully' });
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setSelectedSubmission(null);
      }, 2000);
    } catch (err: any) {
      setRecallMessage({ type: 'error', text: 'Failed to recall initiative' });
      console.error('Error recalling initiative:', err);
    } finally {
      setIsRecalling(false);
    }
  };

  const handleRenotify = async () => {
    if (!selectedSubmission) return;
    try {
      setIsRenotifying(true);
      setRenotifyMessage(null);
      await axios.post(api(`/api/reviews/send-notification/${selectedSubmission.id}`));
      setRenotifyMessage({ type: 'success', text: 'Notification resent to all approvers' });
      setTimeout(() => setRenotifyMessage(null), 3000);
    } catch (err: any) {
      setRenotifyMessage({ type: 'error', text: 'Failed to resend notification' });
    } finally {
      setIsRenotifying(false);
    }
  };

  const handleResubmit = (submission: Initiative) => {
    // Set the initiative data to be edited
    setEditData({
      id: submission.id,
      use_case_name: submission.use_case_name,
      proposed_solution_owner: submission.proposed_solution_owner,
      solution_sponsor: submission.solution_sponsor,
      purpose: submission.purpose,
      value: submission.value,
      users: submission.users,
      solution_scope: submission.solution_scope,
      proposed_workflow: submission.proposed_workflow,
      inputs_and_data_sources: submission.inputs_and_data_sources,
    } as any);
    
    // Close modal and switch to Submit tab
    setSelectedSubmission(null);
    switchToSubmitTab();
  };

  const handleDelete = (submissionId: number) => {
    setDeleteSubmissionId(submissionId);
    setShowDeleteConfirm(true);
  };

  const handleAcceptPartner = async () => {
    if (!acceptModal) return;
    const { initiativeId, requestId } = acceptModal;
    setIsAccepting(true);
    try {
      await axios.patch(api(`/api/initiatives/${initiativeId}/partner-requests/${requestId}`), { action: 'accept', owner_comment: acceptComment });
      setPartnerDetails(prev => ({
        ...prev,
        [initiativeId]: prev[initiativeId].map(r => r.id === requestId ? { ...r, status: 'accepted', owner_comment: acceptComment } : r)
      }));
      try {
        const res = await axios.get(api(`/api/initiatives/engagements/summary?initiative_ids=${initiativeId}`));
        setEngagementSummary(prev => ({ ...prev, ...res.data }));
      } catch { /* silent */ }
    } catch { /* silent */ }
    setIsAccepting(false);
    setAcceptModal(null);
    setAcceptComment('');
  };

  const confirmDelete = async () => {
    if (!deleteSubmissionId) return;

    try {
      setIsDeleting(true);
      await initiativeService.deleteInitiative(deleteSubmissionId);
      
      // Remove from list
      setSubmissions(submissions.filter(sub => sub.id !== deleteSubmissionId));
      
      // Close modal
      setSelectedSubmission(null);
      setShowDeleteConfirm(false);
      setDeleteSubmissionId(null);
      
      // Show success message
      setDisplayMessage({ type: 'success', text: 'Submission deleted successfully' });
      setTimeout(() => setDisplayMessage(null), 3000);
    } catch (error: any) {
      console.error('Error deleting submission:', error);
      setDisplayMessage({ type: 'error', text: 'Failed to delete submission' });
      setShowDeleteConfirm(false);
      setDeleteSubmissionId(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchDelegationFlow = async (submissionId: number) => {
    try {
      setWorkflowSubmissionId(submissionId);
      setShowWorkflowModal(true);
      setIsLoadingWorkflow(true);
      const response = await axios.get(api(`/api/reviews/flow/${submissionId}`));
      setWorkflowData(response.data);
    } catch (error: any) {
      console.error('Error fetching workflow:', error);
      setDisplayMessage({ type: 'error', text: 'Failed to load workflow' });
      setShowWorkflowModal(false);
    } finally {
      setIsLoadingWorkflow(false);
    }
  };

  const formatRelativeTime = (dateString: string): string => {
    // Parse the date string and ensure it's treated as UTC/server time
    let date: Date;
    
    // If the string doesn't have timezone info, assume it's UTC
    if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-')) {
      date = new Date(dateString + 'Z');
    } else {
      date = new Date(dateString);
    }
    
    // Always use current client time for "now"
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays > 0) {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    } else {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`;
    }
  };

  const formatDateWithTime = (dateString: string): string => {
    // Parse the date string - browser will interpret based on locale
    const date = new Date(dateString);
    
    // Format to local timezone date and time
    return date.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const calculateSLADueDate = (dateString: string): string => {
    // Parse the date string and ensure it's treated as UTC/server time
    let date: Date;
    
    // If the string doesn't have timezone info, assume it's UTC
    if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-')) {
      date = new Date(dateString + 'Z');
    } else {
      date = new Date(dateString);
    }
    
    const dueDate = new Date(date.getTime() + 7 * 24 * 60 * 60 * 1000);
    return dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const calculateDaysRemaining = (dateString: string): { days: number; text: string; className: string } => {
    // Parse the date string and ensure it's treated as UTC/server time
    let date: Date;
    
    // If the string doesn't have timezone info, assume it's UTC
    if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-')) {
      date = new Date(dateString + 'Z');
    } else {
      date = new Date(dateString);
    }
    
    const dueDate = new Date(date.getTime() + 7 * 24 * 60 * 60 * 1000);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to midnight for accurate day comparison
    const dueDateOnly = new Date(dueDate);
    dueDateOnly.setHours(0, 0, 0, 0);
    
    const timeDiff = dueDateOnly.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    let text: string;
    let className: string;
    
    if (daysDiff < 0) {
      text = 'Overdue';
      className = 'days-overdue';
    } else if (daysDiff === 0) {
      text = 'Today';
      className = 'days-critical';
    } else if (daysDiff === 1) {
      text = '1 day';
      className = 'days-critical';
    } else if (daysDiff <= 3) {
      text = `${daysDiff} days`;
      className = 'days-warning';
    } else {
      text = `${daysDiff} days`;
      className = 'days-ok';
    }
    
    return { days: daysDiff, text, className };
  };

  const truncateText = (text: string | null | undefined, maxLength: number = 300): string => {
    if (!text) return 'Not provided';
    const str = String(text);
    if (str.length <= maxLength) return str;
    return str.substring(0, maxLength) + '...';
  };

  const handleEditClick = () => {
    // This function is no longer used but kept for reference
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // This function is no longer used but kept for reference
  };

  const handleEditSubmit = async () => {
    // This function is no longer used but kept for reference
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'status-approved';
      case 'rejected':
        return 'status-rejected';
      case 'recalled':
        return 'status-recalled';
      default:
        return 'status-pending';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getPaginatedItems = (items: Initiative[], page: number) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  };

  const getTotalPages = (items: Initiative[]) => {
    return Math.ceil(items.length / itemsPerPage);
  };

  const getFilteredSubmissions = (items: Initiative[], query: string, status: string) => {
    let filtered = items;

    // Search by initiative name
    if (query.trim()) {
      filtered = filtered.filter(i =>
        i.use_case_name.toLowerCase().includes(query.toLowerCase()) ||
        i.proposed_solution_owner.toLowerCase().includes(query.toLowerCase())
      );
    }

    // Filter by status
    if (status !== 'all') {
      filtered = filtered.filter(i => i.status.toLowerCase() === status);
    }

    return filtered;
  };

  if (isLoading) {
    return (
      <div className="my-submissions">
        <Spinner message="Loading your submissions..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-submissions">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="my-submissions">
      
      {displayMessage && (
        <div className={`message-alert ${displayMessage.type}`}>
          {displayMessage.text}
        </div>
      )}
      
      {submissions.length === 0 ? (
        <div className="empty-state">
          <p>You haven't submitted any initiatives yet.</p>
        </div>
      ) : (
        <>
          {/* Filter Controls */}
          <div className="filter-controls">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search by initiative name or owner..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="search-input"
              />
            </div>
            <div className="filter-dropdown">
              <label htmlFor="status-filter">Filter by Status:</label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as 'all' | 'pending' | 'approved' | 'rejected');
                  setCurrentPage(1);
                }}
                className="dropdown"
              >
                <option value="all">All Submissions</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          <div className="submissions-table">
            <table id="my-submissions-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Initiative Name</th>
                  <th>Owner</th>
                  <th>Sponsor</th>
                  <th>Division</th>
                  <th>Submitted</th>
                  <th>Expected Approval</th>
                  <th>Days</th>
                  <th>Status</th>
                  <th>Engagement</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {getPaginatedItems(
                  getFilteredSubmissions(submissions, searchQuery, statusFilter),
                  currentPage
                ).map((submission) => {
                  const isExpanded = expandedRows.has(submission.id);
                  const eng = engagementSummary[String(submission.id)];
                  const hasEngagement = eng && (eng.like_count > 0 || eng.partner_count > 0);
                  const toggleExpand = async () => {
                    if (!hasEngagement) return;
                    setExpandedRows(prev => {
                      const next = new Set(prev);
                      if (next.has(submission.id)) { next.delete(submission.id); }
                      else {
                        next.add(submission.id);
                        // Fetch partner details if not already loaded
                        if (!partnerDetails[submission.id] && eng && eng.partner_count > 0) {
                          axios.get(api(`/api/initiatives/${submission.id}/partner-requests`))
                            .then(r => setPartnerDetails(pd => ({ ...pd, [submission.id]: r.data.requests || [] })))
                            .catch(() => {});
                        }
                      }
                      return next;
                    });
                  };
                  return (
                  <React.Fragment key={submission.id}>
                  <tr
                    className="submission-row"
                  >
                    <td className="id-cell">
                      <span
                        onClick={hasEngagement ? toggleExpand : undefined}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          cursor: hasEngagement ? 'pointer' : 'default'
                        }}
                      >
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 14, height: 14, borderRadius: 2,
                          border: hasEngagement ? '1px solid #aaa' : '1px solid #ddd',
                          color: hasEngagement ? '#444' : '#ccc',
                          fontSize: 12, lineHeight: 1, flexShrink: 0,
                          userSelect: 'none'
                        }}>
                          {hasEngagement ? (isExpanded ? '−' : '+') : '−'}
                        </span>
                        #{submission.id}
                      </span>
                    </td>
                    <td className="name-cell">{submission.use_case_name}</td>
                    <td>{submission.proposed_solution_owner}</td>
                    <td>{submission.solution_sponsor}</td>
                    <td>{submission.division || '—'}</td>
                    <td>{formatDate(submission.submitted_date)}</td>
                    <td>{calculateSLADueDate(submission.submitted_date)}</td>
                    <td>
                      <span className={`days-remaining ${calculateDaysRemaining(submission.submitted_date).className}`}>
                        {calculateDaysRemaining(submission.submitted_date).text}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusBadgeClass(submission.status)}`}>
                        {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                      </span>
                    </td>
                    <td style={{ textAlign: 'left' }}>
                      {(() => {
                        const eng = engagementSummary[String(submission.id)];
                        if (!eng || (eng.like_count === 0 && eng.partner_count === 0)) {
                          return <span style={{ color: '#adb5bd', fontSize: 12 }}>—</span>;
                        }
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                            {eng.like_count > 0 && (
                              <span
                                title={eng.liked_by.length > 0 ? `Liked by: ${eng.liked_by.join(', ')}` : undefined}
                                style={{ cursor: eng.liked_by.length > 0 ? 'help' : 'default', color: '#475467' }}
                              >
                                👍 {eng.like_count} {eng.like_count === 1 ? 'Like' : 'Likes'}
                              </span>
                            )}
                            {eng.partner_count > 0 && (
                              <span style={{ color: '#475467' }}>
                                🤝 {eng.partner_count} Partnership {eng.partner_count === 1 ? 'Request' : 'Requests'}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
                        <button
                          className="btn-icon-review"
                          onClick={() => setSelectedSubmission(submission)}
                          title="View details"
                        >
                          <img src="/viewdetails.png" alt="View details" />
                        </button>
                        <button
                          className="btn-icon-workflow"
                          onClick={() => fetchDelegationFlow(submission.id)}
                          title="View workflow"
                        >
                          <img src="/viewworkflow.png" alt="View workflow" />
                        </button>
                        <button
                          className="btn-icon-delete"
                          onClick={() => handleDelete(submission.id)}
                          title="Delete submission"
                        >
                          <img src="/deletesubmission.png" alt="Delete submission" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && hasEngagement && (
                    <tr className="submission-row-expanded">
                      <td colSpan={11} style={{ padding: 0, background: '#f9f9f9', borderBottom: '1px solid #e0e0e0' }}>
                        <div style={{ padding: '12px 16px 14px 32px', display: 'flex', gap: 40, flexWrap: 'wrap' }}>
                          {eng.liked_by.length > 0 && (
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 11, color: '#888', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Liked by</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {eng.liked_by.map(email => (
                                  <span key={email} style={{ fontSize: 13, color: '#222' }}>{email}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {eng.partner_count > 0 && (
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 11, color: '#888', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Partnership Requests</div>
                              {partnerDetails[submission.id] ? (
                                partnerDetails[submission.id].length === 0
                                  ? <span style={{ fontSize: 13, color: '#888' }}>No details available</span>
                                  : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                      {partnerDetails[submission.id].map((r, i) => (
                                        <div key={i} style={{ fontSize: 13, color: '#222', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                          <div>
                                            <span style={{ fontWeight: 500 }}>{r.email}</span>
                                            {r.message && <span style={{ color: '#888', marginLeft: 8 }}>— {r.message}</span>}
                                          </div>
                                          {r.status === 'accepted' ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                              <span style={{ fontSize: 11, fontWeight: 600, color: '#555', background: '#ececec', borderRadius: 4, padding: '2px 8px', letterSpacing: '0.03em', display: 'inline-block' }}>Partnership Accepted</span>
                                              {r.owner_comment && <span style={{ fontSize: 12, color: '#666', fontStyle: 'italic' }}>{r.owner_comment}</span>}
                                            </div>
                                          ) : submission.submitted_by?.toLowerCase() === user?.email?.toLowerCase() ? (
                                            <button
                                              onClick={e => { e.stopPropagation(); setAcceptComment(''); setAcceptModal({ initiativeId: submission.id, requestId: r.id }); }}
                                              style={{ fontSize: 11, padding: '2px 10px', border: '1px solid #c00', borderRadius: 4, background: '#fff', color: '#c00', cursor: 'pointer', fontWeight: 600 }}
                                            >Accept</button>
                                          ) : null}
                                        </div>
                                      ))}
                                    </div>
                              ) : <span style={{ fontSize: 13, color: '#aaa' }}>Loading...</span>}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {getTotalPages(getFilteredSubmissions(submissions, searchQuery, statusFilter)) > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>
              <div className="pagination-info">
                Page {currentPage} of {getTotalPages(getFilteredSubmissions(submissions, searchQuery, statusFilter))}
              </div>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(Math.min(getTotalPages(getFilteredSubmissions(submissions, searchQuery, statusFilter)), currentPage + 1))}
                disabled={currentPage === getTotalPages(getFilteredSubmissions(submissions, searchQuery, statusFilter))}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {selectedSubmission && (
        <div className="modal-overlay" onClick={() => setSelectedSubmission(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>{selectedSubmission.use_case_name}</h3>
                <p className="submission-id">ID: {selectedSubmission.id}</p>
              </div>
              <button className="close-btn" onClick={() => setSelectedSubmission(null)}>×</button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <div className="detail-row">
                  <span className="label">Use Case Name:</span>
                  <span className="value">{selectedSubmission.use_case_name}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Submitted By:</span>
                  <span className="value">{selectedSubmission.submitted_by}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Status:</span>
                  <span className={`value status-badge ${getStatusBadgeClass(selectedSubmission.status)}`}>
                    {selectedSubmission.status.charAt(0).toUpperCase() + selectedSubmission.status.slice(1)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Submitted Date:</span>
                  <span className="value">{formatDate(selectedSubmission.submitted_date)}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Division:</span>
                  <span className="value">{selectedSubmission.division || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Solution Owner:</span>
                  <span className="value">{selectedSubmission.proposed_solution_owner}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Solution Sponsor:</span>
                  <span className="value">{selectedSubmission.solution_sponsor}</span>
                </div>
                {selectedSubmission.approval_date && (
                  <div className="detail-row">
                    <span className="label">Approval Date:</span>
                    <span className="value">{formatDate(selectedSubmission.approval_date)}</span>
                  </div>
                )}
                {selectedSubmission.approved_by && (
                  <div className="detail-row">
                    <span className="label">Approved By:</span>
                    <span className="value">{selectedSubmission.approved_by}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="label">Purpose:</span>
                  <span className="value">{truncateText(selectedSubmission.purpose)}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Value/Benefits:</span>
                  <span className="value">{truncateText(selectedSubmission.value)}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Target Users:</span>
                  <span className="value">{truncateText(selectedSubmission.users)}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Solution Scope:</span>
                  <span className="value">{truncateText(selectedSubmission.solution_scope)}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Proposed Workflow:</span>
                  <span className="value">{truncateText(selectedSubmission.proposed_workflow)}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Inputs & Data Sources:</span>
                  <span className="value">{truncateText(selectedSubmission.inputs_and_data_sources)}</span>
                </div>
              </div>

              {selectedSubmission.admin_comment && (
                <div style={{ marginBottom: '20px' }} className="admin-comment">
                  <h4>Admin Comment:</h4>
                  <p>{truncateText(selectedSubmission.admin_comment)}</p>
                </div>
              )}
              {selectedSubmission.division && (
                <div style={{ marginBottom: '20px', padding: '12px', background: '#f0f0f0', borderRadius: '4px' }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '13px' }}>Notifications will be sent to:</h4>
                  {isLoadingReviewers ? (
                    <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>Loading reviewers...</p>
                  ) : divisionReviewers.length > 0 ? (
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px' }}>
                      {divisionReviewers.map((reviewer: any) => (
                        <li key={reviewer.id}>{reviewer.email} ({reviewer.display_name || 'N/A'})</li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ margin: 0, fontSize: '12px', color: '#999' }}>No reviewers configured for this division</p>
                  )}
                </div>
              )}
              {recallMessage && (
                <div className={`message ${recallMessage.type}`}>
                  {recallMessage.text}
                </div>
              )}
              {renotifyMessage && (
                <div className={`message ${renotifyMessage.type}`}>
                  {renotifyMessage.text}
                </div>
              )}
            </div>

            <div className="modal-footer">
              {selectedSubmission.status.toLowerCase() === 'pending' && (
                <button
                  className="btn-recall"
                  onClick={handleRecall}
                  disabled={isRecalling}
                >
                  {isRecalling ? 'Recalling...' : 'Recall Initiative'}
                </button>
              )}
              {selectedSubmission.status.toLowerCase() === 'rejected' && (
                <button
                  className="btn-edit"
                  onClick={() => handleResubmit(selectedSubmission)}
                >
                  Resubmit
                </button>
              )}
              <button
                className="btn-renotify"
                onClick={handleRenotify}
                disabled={isRenotifying}
                title="Resend notification email"
              >
                {isRenotifying ? 'Sending...' : '✉️ Renotify'}
              </button>
              <button
                className="btn-delete"
                onClick={() => handleDelete(selectedSubmission.id)}
                disabled={isDeleting}
              >
                🗑️ Delete
              </button>
              <button
                className="btn-close"
                onClick={() => {
                  setSelectedSubmission(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Workflow Modal */}
      {showWorkflowModal && (
        <div className="modal-overlay" onClick={() => !isLoadingWorkflow && (setShowWorkflowModal(false), setWorkflowSubmissionId(null), setWorkflowData(null))}>
          <div className="modal-content" style={{ maxWidth: '1000px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Workflow</h3>
              <button className="modal-close" onClick={() => !isLoadingWorkflow && (setShowWorkflowModal(false), setWorkflowSubmissionId(null), setWorkflowData(null))} disabled={isLoadingWorkflow}>×</button>
            </div>

            <div className="modal-body">
              {isLoadingWorkflow ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '60px 40px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '20px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '3px solid #e0e0e0',
                    borderTop: '3px solid #dc3545',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <div style={{ color: '#666', fontSize: '15px', fontWeight: '500' }}>Loading workflow...</div>
                </div>
              ) : workflowData ? (
                <>
                  {/* Check if initiative has been approved/rejected */}
                  {workflowData.approval_status ? (
                    <div>
                      {/* Show submission and approval timeline info as horizontal flow */}
                      <div style={{
                        marginTop: '15px',
                        display: 'flex',
                        gap: '15px',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexWrap: 'nowrap',
                        overflowX: 'auto'
                      }}>
                        <div style={{
                          padding: '12px 16px',
                          backgroundColor: '#e3f2fd',
                          borderLeft: '4px solid #2196f3',
                          borderRadius: '4px',
                          fontSize: '12px',
                          minWidth: '200px'
                        }}>
                          <div style={{ color: '#1565c0', fontWeight: '600', marginBottom: '4px' }}>Request Submitted</div>
                          <div style={{ color: '#666', fontSize: '11px', marginBottom: '6px' }}>
                            {workflowSubmissionId ? (submissions.find(s => s.id === workflowSubmissionId)?.submitted_by || 'N/A') : 'N/A'}
                          </div>
                          <div style={{ color: '#333', fontSize: '11px' }}>
                            {workflowData?.submitted_date ? formatDateWithTime(workflowData.submitted_date) : 'N/A'}
                          </div>
                        </div>
                        <div style={{ color: '#999', fontSize: '18px', fontWeight: '300' }}>→</div>
                        <div style={{
                          padding: '12px 16px',
                          backgroundColor: workflowData.approval_status === 'approved' ? '#e8f5e9' : '#ffebee',
                          borderLeft: `4px solid ${workflowData.approval_status === 'approved' ? '#4caf50' : '#f44336'}`,
                          borderRadius: '4px',
                          fontSize: '12px',
                          minWidth: '200px'
                        }}>
                          <div style={{ color: workflowData.approval_status === 'approved' ? '#2e7d32' : '#ee0000', fontWeight: '600', marginBottom: '4px' }}>
                            {workflowData.approval_status === 'approved' ? 'Approved' : 'Rejected'}
                          </div>
                          <div style={{ color: '#666', fontSize: '11px', marginBottom: '6px' }}>
                            {workflowData.approved_by || 'N/A'}
                          </div>
                          <div style={{ color: '#333', fontSize: '11px' }}>
                            {workflowData.approval_date ? formatDateWithTime(workflowData.approval_date) : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : workflowData.flow && workflowData.flow.length > 0 ? (
                    <div>
                      {/* Show delegation workflow */}
                      <div className="horizontal-timeline">
                        {/* Show initial requestor */}
                        <div className="timeline-box" style={{ backgroundColor: '#fff3e0', borderColor: '#ff9800', width: '140px' }}>
                          <div className="timeline-reviewer" style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>Initiated By</div>
                          <div className="timeline-reviewer" style={{ fontSize: '12px', marginBottom: '4px' }}>{workflowData?.submitted_by_name || workflowData?.submitted_by || 'N/A'}</div>
                          <div className="timeline-date">{workflowData?.submitted_date ? formatDateWithTime(workflowData.submitted_date) : 'N/A'}</div>
                        </div>
                        <div className="timeline-arrow">→</div>
                        {workflowData.flow.map((step: any, index: number) => (
                          <React.Fragment key={step.step}>
                            <div className="timeline-box" style={{ width: '140px' }}>
                              <div className="timeline-reviewer" style={{ fontSize: '12px', marginBottom: '4px' }}>From: {step.from_name}</div>
                              <div style={{ fontSize: '11px', marginBottom: '6px' }}>↓</div>
                              <div className="timeline-reviewer" style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>To: {step.to_name}</div>
                              <div className="timeline-email">{step.to_email}</div>
                              <div className="timeline-date">{formatRelativeTime(step.delegated_date)}</div>
                            </div>
                            {index < workflowData.flow.length - 1 && (
                              <div className="timeline-arrow">→</div>
                            )}
                          </React.Fragment>
                        ))}
                        {workflowData.current_reviewer_email && (
                          <>
                            <div className="timeline-arrow">→</div>
                            <div className="timeline-box" style={{ 
                              borderColor: '#17a2b8', 
                              backgroundColor: '#e3f2fd',
                              fontWeight: '600',
                              width: '140px'
                            }}>
                              <div className="timeline-reviewer">Currently With</div>
                              <div className="timeline-reviewer">{workflowData.current_reviewer_name || workflowData.current_reviewer_email}</div>
                              <div className="timeline-email">{workflowData.current_reviewer_email}</div>
                              <div className="timeline-date">Now</div>
                            </div>
                          </>
                        )}
                      </div>
                      <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ color: '#999', fontSize: '16px' }}>→</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <div style={{ color: '#333', fontWeight: '600', fontSize: '13px' }}>
                            Expected Decision: {workflowSubmissionId ? calculateSLADueDate(submissions.find(s => s.id === workflowSubmissionId)?.submitted_date || '') : 'N/A'}
                          </div>
                          <div style={{ color: '#666', fontSize: '11px' }}>Most decisions within a week</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '30px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                      <p style={{ fontSize: '16px', color: '#666', margin: '0 0 20px 0' }}>
                        <strong>Pending Review</strong>
                      </p>
                      <div style={{
                        display: 'flex',
                        gap: '15px',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexWrap: 'nowrap',
                        overflowX: 'auto'
                      }}>
                        <div style={{
                          padding: '12px 16px',
                          backgroundColor: '#e3f2fd',
                          borderLeft: '4px solid #2196f3',
                          borderRadius: '4px',
                          fontSize: '12px',
                          minWidth: '200px'
                        }}>
                          <div style={{ color: '#1565c0', fontWeight: '600', marginBottom: '4px' }}>Request Submitted</div>
                          <div style={{ color: '#666', fontSize: '11px', marginBottom: '6px' }}>
                            {workflowData?.submitted_by_name || workflowData?.submitted_by || 'N/A'}
                          </div>
                          <div style={{ color: '#333', fontSize: '11px' }}>
                            {workflowData?.submitted_date ? formatDateWithTime(workflowData.submitted_date) : 'N/A'}
                          </div>
                        </div>
                        <div style={{ color: '#999', fontSize: '18px', fontWeight: '300' }}>→</div>
                        <div style={{
                          padding: '12px 16px',
                          backgroundColor: '#fff9e6',
                          borderLeft: '4px solid #ffc107',
                          borderRadius: '4px',
                          fontSize: '12px',
                          minWidth: '200px'
                        }}>
                          <div style={{ color: '#856404', fontWeight: '600', marginBottom: '4px' }}>Awaiting Assignment</div>
                          <div style={{ color: '#856404', fontSize: '11px', marginBottom: '6px' }}>In Review Queue</div>
                          <div style={{ color: '#dc3545', fontWeight: '500', fontSize: '11px' }}>PENDING</div>
                        </div>
                      </div>
                    </div>
                  )}
            </>
          ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  Unable to load workflow data
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deleteSubmissionId && (
        <div className="modal-overlay" onClick={() => !isDeleting && setShowDeleteConfirm(false)}>
          <div className="modal-content" style={{ maxWidth: '400px', maxHeight: '200px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ padding: '12px 16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>Confirm Delete</h3>
              <button className="modal-close" onClick={() => !isDeleting && setShowDeleteConfirm(false)} disabled={isDeleting} style={{ width: '28px', height: '28px', fontSize: '20px' }}>×</button>
            </div>
            <div className="modal-body" style={{ padding: '12px 16px' }}>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '14px', color: '#333', margin: '0 0 12px 0', lineHeight: '1.4' }}>
                  Are you sure you want to delete submission #{deleteSubmissionId}?
                </p>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  <button
                    className="btn btn-danger"
                    onClick={confirmDelete}
                    disabled={isDeleting}
                    style={{ flex: 1, maxWidth: '120px', padding: '8px 12px', fontSize: '13px' }}
                  >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={isDeleting}
                    style={{ flex: 1, maxWidth: '120px', padding: '8px 12px', fontSize: '13px' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {acceptModal && (
        <div className="modal-overlay" onClick={() => !isAccepting && setAcceptModal(null)}>
          <div className="modal-content" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>Accept Partnership Request</h3>
              <button className="close-btn" onClick={() => !isAccepting && setAcceptModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>
                Optionally add a response comment to the requester.
              </p>
              <textarea
                style={{ width: '100%', minHeight: 80, fontSize: 13, padding: '8px 10px', border: '1px solid #d0d5dd', borderRadius: 6, resize: 'vertical', boxSizing: 'border-box' }}
                placeholder="e.g. Great idea — let's connect to align on data requirements."
                value={acceptComment}
                onChange={e => setAcceptComment(e.target.value)}
                disabled={isAccepting}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '12px 20px 16px' }}>
              <button
                onClick={() => setAcceptModal(null)}
                disabled={isAccepting}
                style={{ padding: '7px 18px', fontSize: 13, border: '1px solid #d0d5dd', borderRadius: 6, background: '#fff', cursor: 'pointer' }}
              >Cancel</button>
              <button
                onClick={handleAcceptPartner}
                disabled={isAccepting}
                style={{ padding: '7px 18px', fontSize: 13, border: 'none', borderRadius: 6, background: '#c00', color: '#fff', cursor: isAccepting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
              >
                {isAccepting && <span style={{ width: 12, height: 12, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />}
                {isAccepting ? 'Accepting...' : 'Accept'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MySubmissions;
